// =============================================================================
// RESTAURANT SCHEMA
// =============================================================================
// This file defines the data model for a restaurant's core information.
// Everything the restaurant owner fills in (name, address, hours, etc.)
// is validated against this schema to catch errors early — before the
// site is built and deployed.
//
// HOW IT WORKS:
// 1. The restaurant owner (or you as the developer) fills in a JSON file
//    like `content/restaurant.json`
// 2. At build time, this schema validates that JSON — if something is
//    missing or wrong (e.g., invalid phone format), the build fails with
//    a clear error message instead of deploying a broken site.
// 3. TypeScript types are automatically inferred from the schema, so
//    components get full autocomplete and type safety.
//
// WHAT IS Zod?
// Zod is a TypeScript-first validation library. You define a "schema"
// (a set of rules), and Zod checks that data matches those rules at runtime.
// Unlike TypeScript types (which only exist at compile time and disappear
// in production), Zod validation runs in the actual code — so it catches
// invalid JSON data that TypeScript alone can't.
// =============================================================================

import { z } from 'zod';

// -----------------------------------------------------------------------------
// Reusable Sub-Schemas
// (Small building blocks used inside the main Restaurant schema)
// -----------------------------------------------------------------------------

/**
 * Postal address — following schema.org PostalAddress format.
 * This structure is also used directly in the JSON-LD structured data
 * that search engines and AI assistants read.
 */
export const PostalAddressSchema = z.object({
  streetAddress: z.string().min(1, 'Street address is required'),
  addressLocality: z.string().min(1, 'City/town is required'),
  addressRegion: z.string().optional(), // State/province — optional for countries without states
  postalCode: z.string().min(1, 'Postal/ZIP code is required'),
  addressCountry: z
    .string()
    .length(2, 'Use 2-letter country code (ISO 3166-1 alpha-2), e.g. "US", "FR", "BR"'),
});
export type PostalAddress = z.infer<typeof PostalAddressSchema>;

/**
 * Geographic coordinates for the restaurant's location.
 * Used for map pins and schema.org GeoCoordinates.
 *
 * TIP: Get these from Google Maps — right-click the pin → "Copy coordinates"
 */
export const GeoCoordinatesSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});
export type GeoCoordinates = z.infer<typeof GeoCoordinatesSchema>;

/**
 * Opening hours for a specific set of days.
 * Follows schema.org OpeningHoursSpecification format.
 *
 * EXAMPLE:
 * { dayOfWeek: ["Monday", "Tuesday", "Wednesday"], opens: "11:30", closes: "22:00" }
 * { dayOfWeek: ["Saturday"], opens: "10:00", closes: "23:00" }
 */
const DayOfWeek = z.enum([
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]);

export const OpeningHoursSpecSchema = z.object({
  dayOfWeek: z.array(DayOfWeek).min(1, 'At least one day is required'),
  opens: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM format (00:00 - 23:59), e.g. "09:00"'),
  closes: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM format (00:00 - 23:59), e.g. "22:00"'),
});
export type OpeningHoursSpec = z.infer<typeof OpeningHoursSpecSchema>;

/**
 * A link to a social media profile.
 * The "platform" field controls which icon is shown on the website.
 */
export const SocialLinkSchema = z.object({
  platform: z.enum([
    'instagram',
    'facebook',
    'twitter',
    'tiktok',
    'youtube',
    'linkedin',
    'tripadvisor',
    'yelp',
    'google',
  ]),
  url: z.string().url('Must be a valid URL'),
});
export type SocialLink = z.infer<typeof SocialLinkSchema>;

/**
 * A gallery image with required alt text (for accessibility & SEO).
 * Alt text describes the image for screen readers and search engines.
 */
export const GalleryImageSchema = z.object({
  src: z.string().min(1, 'Image path or URL is required'),
  alt: z.string().min(1, 'Alt text is required for accessibility — describe what the image shows'),
  caption: z.string().optional(),
});
export type GalleryImage = z.infer<typeof GalleryImageSchema>;

/**
 * A press mention or award badge (e.g., "As seen in The Guardian").
 * Shown as a horizontal strip of logos on the homepage.
 */
export const PressMentionSchema = z.object({
  name: z.string().min(1, 'Publication/award name is required'),
  logo: z.string().min(1, 'Logo image path is required'),
  url: z.string().url().optional(), // Link to the article (if available)
});
export type PressMention = z.infer<typeof PressMentionSchema>;

/**
 * A customer review or testimonial (e.g. from Google Maps, TripAdvisor).
 * Shown in the customer testimonials section.
 */
export const ReviewItemSchema = z.object({
  id: z.string().min(1, 'Review ID is required'),
  author: z.string().min(1, 'Author name is required'),
  rating: z.number().min(1).max(5).default(5),
  text: z.string().min(1, 'Review text is required'),
  date: z.string().optional(),
  source: z.enum(['google', 'tripadvisor', 'thefork', 'yelp', 'custom']).default('google'),
  authorPhoto: z.string().optional(),
  url: z.string().url().optional(),
  highlight: z.boolean().optional(),
});
export type ReviewItem = z.infer<typeof ReviewItemSchema>;

/**
 * Reviews and overall rating configuration.
 */
export const ReviewsConfigSchema = z.object({
  summary: z.object({
    rating: z.number().min(1).max(5),
    reviewCount: z.number().int().nonnegative(),
    platform: z.enum(['google', 'tripadvisor', 'multi']).default('google'),
    url: z.string().url().optional(),
  }),
  items: z.array(ReviewItemSchema).default([]),
});
export type ReviewsConfig = z.infer<typeof ReviewsConfigSchema>;

// -----------------------------------------------------------------------------
// Feature Toggles
// (Turn modules on/off per restaurant — keeps sites lean)
// -----------------------------------------------------------------------------

/**
 * Feature flags that control which sections/modules appear on the website.
 *
 * WHY THIS MATTERS:
 * Not every restaurant needs every feature. A small café doesn't need
 * online ordering or multi-location support. By toggling features off,
 * the generated site is smaller, faster, and simpler.
 *
 * These flags are also used by the CSP builder to only allow third-party
 * scripts for features that are actually enabled (better security).
 */
export const FeatureTogglesSchema = z.object({
  /** Show a reservation/booking widget (OpenTable, Resy, SevenRooms, etc.) */
  reservations: z.boolean().default(false),
  /** Enable online ordering with a full cart/checkout flow */
  onlineOrdering: z.boolean().default(false),
  /** Show delivery platform buttons (Uber Eats, Deliveroo, etc.) */
  delivery: z.boolean().default(false),
  /** Show a gift card purchase section */
  giftCards: z.boolean().default(false),
  /** Show an email newsletter signup form */
  newsletter: z.boolean().default(true),
  /** Show a photo gallery section */
  gallery: z.boolean().default(true),
  /** Show a customer reviews / testimonials section */
  reviews: z.boolean().default(true),
  /** Enable a blog/news section */
  blog: z.boolean().default(false),
  /** Enable multi-location store locator (for restaurant groups) */
  multiLocation: z.boolean().default(false),
  /** Allow users to toggle dark mode */
  darkMode: z.boolean().default(false),
  /** Use a looping background video in the hero section */
  videoHero: z.boolean().default(false),
  /** Show a GDPR cookie consent banner — auto-enabled for EU regions */
  cookieConsent: z.boolean().default(true),
});
export type FeatureToggles = z.infer<typeof FeatureTogglesSchema>;

// -----------------------------------------------------------------------------
// Main Restaurant Schema
// -----------------------------------------------------------------------------

/**
 * The complete data model for a restaurant.
 * This is the single source of truth that every component reads from.
 *
 * USAGE:
 * 1. Create a file: apps/site/content/restaurant.json
 * 2. Fill it in following this schema's structure
 * 3. The build will validate it and pass it to all components
 */
export const RestaurantSchema = z.object({
  // --- Identity ---
  /** The restaurant's display name (e.g., "Trattoria da Marco") */
  name: z.string().min(1, 'Restaurant name is required'),

  /** A short tagline shown under the name in the hero section
   *  (e.g., "Farm-to-table Italian cuisine since 1985") */
  tagline: z.string().optional(),

  /** Types of cuisine served — used in schema.org structured data
   *  and helps search engines categorize the restaurant
   *  (e.g., ["Italian", "Mediterranean"]) */
  cuisineTypes: z.array(z.string()).min(1, 'At least one cuisine type is required'),

  /** Price range indicator — shown in search results and structured data
   *  "$" = budget, "$$" = moderate, "$$$" = upscale, "$$$$" = fine dining */
  priceRange: z.enum(['$', '$$', '$$$', '$$$$']),

  // --- Contact ---
  /** Phone number in international format (e.g., "+33 1 42 36 53 13") */
  phone: z.string().min(1, 'Phone number is required'),

  /** Contact email address */
  email: z.string().email('Must be a valid email address'),

  /** Full mailing address */
  address: PostalAddressSchema,

  /** GPS coordinates for map pins */
  geo: GeoCoordinatesSchema,

  /** When the restaurant is open */
  hours: z.array(OpeningHoursSpecSchema).min(1, 'At least one set of opening hours is required'),

  /** Links to social media profiles */
  socialLinks: z.array(SocialLinkSchema).default([]),

  // --- Optional Content Sections ---

  /** The "About Us" / "Our Story" section — builds trust with customers */
  about: z
    .object({
      /** The restaurant's story (HTML or markdown is fine) */
      story: z.string().min(1),
      /** Head chef's name (optional) */
      chefName: z.string().optional(),
      /** Short chef biography (optional) */
      chefBio: z.string().optional(),
      /** Chef photo path (optional) */
      chefImage: z.string().optional(),
    })
    .optional(),

  /** Photo gallery images */
  gallery: z.array(GalleryImageSchema).default([]),

  /** Press mentions and awards */
  press: z.array(PressMentionSchema).default([]),

  /** Customer reviews and overall rating */
  reviews: ReviewsConfigSchema.optional(),

  /** Feature toggle flags — controls which modules are active */
  features: FeatureTogglesSchema.default({}),
});

/**
 * TypeScript type automatically generated from the Zod schema.
 *
 * HOW THIS WORKS:
 * z.infer<typeof RestaurantSchema> tells TypeScript to look at the Zod
 * schema and create a matching TypeScript type. This means:
 * - If you add a field to the schema, the type updates automatically
 * - Components that use this type get autocomplete for all fields
 * - TypeScript will show errors if you try to access a field that doesn't exist
 */
export type Restaurant = z.infer<typeof RestaurantSchema>;
